import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    NJAutocomplete,
    NJButton,
    NJFormItem,
    NJIcon,
    NJIconButton,
    NJInlineMessage,
    NJModal,
    NJModalContentInformation,
    NJSegmentedControl,
    NJSegmentedControlButton,
    NJSelect,
    NJText,
    NJToastContainer,
    NJTooltip,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import Loading from '@shared/Loading';
import { Size2MixAccordion, Size2MixInfoGrid, Size2MixSection, Size2MixStatusTag, NetworkShell, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { useGeneralOptions, useNetworkGeneral, useResetNetworkGeneral, useUpdateNetworkGeneral } from './hooks';
import { computePermissions, isViewerId, VIEWER_IDS, VIEWERS } from './permissions';
import { type AutocompleteItem, AccessList, AccessTag, AddWithAutocomplete, InlineEditable, errorToast, submitWithToast, successToast } from './fields';
import type { NetworkGeneralInfo, NetworkGeneralPatch, NetworkPermissions, NetworkStatus, StudyPhase, ViewerId } from './types';

export const meta = {
    title: 'Référence — Réseau > Général',
    description:
        'Page Général du réseau test PUC en mode édition (propriétaire ou groupe « Modifiable par ») et en lecture seule (« Consultable par »). Sélecteur « Voir en tant que » ou ?as=owner|write|read.',
    tags: ['network', 'detail', 'reference'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'general',
    createdAt: '2026-10-10',
};

const STUDY_PHASES: StudyPhase[] = ['Développement', 'Construction', 'Opération', 'Autre'];
const STUDY_PHASE_VARIANT = { Développement: 'information', Construction: 'planet', Opération: 'discovery', Autre: 'neutral' } as const;
const STATUSES: NetworkStatus[] = ['Actif', 'Archivé'];
const DESCRIPTION_MAX = 255;

function notInPrototype() {
    successToast('Action hors périmètre de ce prototype');
}

function referenceTooltip(network: NetworkGeneralInfo, perms: NetworkPermissions) {
    if (!network.contract) return 'La promotion en référence nécessite un contrat associé';
    if (network.isReference) return 'Ce réseau est la référence de son contrat';
    if (!perms.canWrite) return "Vous n'avez pas les droits pour promouvoir ce réseau";
    return 'Promouvoir en réseau de référence';
}

/** Prototype-only banner (not part of Nemo): switch the persona viewing the page. */
function ViewerSwitcher({ viewer, onChange, onReset, perms }: { viewer: ViewerId; onChange: (v: ViewerId) => void; onReset: () => void; perms?: NetworkPermissions }) {
    const [open, setOpen] = useState(true);
    const v = VIEWERS[viewer];
    let rights = 'Aucun accès';
    if (perms?.canEdit) rights = 'Édition';
    else if (perms?.canRead) rights = 'Lecture seule';
    return (
        <aside
            aria-label="Contrôles du prototype"
            style={{
                position: 'fixed',
                right: 24,
                bottom: 56,
                zIndex: 1000,
                width: open ? 340 : 'auto',
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                border: '2px dashed var(--nj-semantic-color-border-brand-moderate-default)',
                borderRadius: 'var(--nj-semantic-size-border-radius-md, 8px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px 8px 16px' }}>
                <NJIcon name="science" size="sm" />
                <NJText scale="sm" style={{ fontWeight: 700, flex: 1 }}>
                    Prototype — {open ? 'Voir en tant que' : VIEWERS[viewer].label}
                </NJText>
                <NJIconButton
                    icon={open ? 'expand_more' : 'expand_less'}
                    label={open ? 'Réduire les contrôles du prototype' : 'Ouvrir les contrôles du prototype'}
                    size="sm"
                    onClick={() => setOpen((o) => !o)}
                />
            </div>
            {open && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 16px 16px' }}>
                    <NJSegmentedControl label="Voir en tant que" size="sm" value={viewer} onChange={(value) => isViewerId(value) && onChange(value)}>
                        {VIEWER_IDS.map((id) => (
                            <NJSegmentedControlButton key={id} label={VIEWERS[id].label} value={id} />
                        ))}
                    </NJSegmentedControl>
                    <NJText scale="sm" variant="secondary">
                        {v.description} Droits calculés : <strong>{rights}</strong>.
                    </NJText>
                    <div>
                        <NJButton label="Réinitialiser les données" variant="secondary" emphasis="minimal" scale="sm" icon="restart_alt" onClick={onReset} />
                    </div>
                </div>
            )}
        </aside>
    );
}

export default function RefNetworkGeneral() {
    const nav = useSize2MixRefNavigation();
    const [searchParams, setSearchParams] = useSearchParams();
    const asParam = searchParams.get('as');
    const viewerId: ViewerId = isViewerId(asParam) ? asParam : 'owner';
    const viewer = VIEWERS[viewerId];

    const { data: network, isLoading } = useNetworkGeneral();
    const { data: options } = useGeneralOptions();
    const update = useUpdateNetworkGeneral(viewerId);
    const reset = useResetNetworkGeneral();

    const [draftName, setDraftName] = useState('');
    const [draftDescription, setDraftDescription] = useState('');
    const [draftTimezone, setDraftTimezone] = useState<AutocompleteItem | undefined>();
    const [draftStatus, setDraftStatus] = useState<NetworkStatus>('Actif');
    const [draftPhase, setDraftPhase] = useState<StudyPhase>('Autre');
    const [confirmUnlink, setConfirmUnlink] = useState(false);

    const perms = network ? computePermissions(network, viewer) : undefined;
    const canEdit = Boolean(perms?.canEdit);
    const patch = (p: NetworkGeneralPatch) => update.mutateAsync(p);

    const setViewer = (id: ViewerId) => {
        const next = new URLSearchParams(searchParams);
        next.set('as', id);
        setSearchParams(next, { replace: true });
    };

    const removeAccess = (field: 'owners' | 'readers' | 'editors', value: string) => {
        if (!network) return;
        const nextItems = network[field].filter((v) => v !== value);
        if (field === 'owners' && nextItems.length === 0) {
            errorToast('Au moins un propriétaire est requis');
            return;
        }
        void submitWithToast(() => patch({ [field]: nextItems }), field === 'owners' ? 'Propriétaire retiré avec succès' : 'Rôle retiré avec succès');
    };
    const addAccess = (field: 'owners' | 'readers' | 'editors', value: string) =>
        submitWithToast(() => patch({ [field]: [...(network?.[field] ?? []), value] }), field === 'owners' ? 'Propriétaire ajouté avec succès' : 'Rôle ajouté avec succès');

    const toolbar = network && perms && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: 16, borderBottom: '1px solid var(--nj-core-color-grey-300)' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <NJTooltip text="Ancienne page réseau">
                    <NJIconButton icon="view_timeline" label="Ancienne page réseau" variant="tertiary" scale="lg" onClick={notInPrototype} />
                </NJTooltip>
                {perms.canRead && (
                    <>
                        <span style={{ height: 24, borderRight: '1px solid var(--nj-core-color-grey-300)' }} />
                        <NJTooltip text={network.isFavorite ? 'Supprimer des favoris' : 'Ajouter aux favoris'}>
                            <NJIconButton
                                icon={network.isFavorite ? 'favorite' : 'favorite_border'}
                                label={network.isFavorite ? 'Supprimer des favoris' : 'Ajouter aux favoris'}
                                variant="tertiary"
                                scale="lg"
                                onClick={() =>
                                    void submitWithToast(
                                        () => patch({ isFavorite: !network.isFavorite }),
                                        network.isFavorite ? 'Réseau retiré des favoris' : 'Réseau ajouté aux favoris',
                                    )
                                }
                            />
                        </NJTooltip>
                        <NJTooltip text={referenceTooltip(network, perms)}>
                            <span>
                                <NJIconButton
                                    icon="emoji_events"
                                    label={referenceTooltip(network, perms)}
                                    variant="tertiary"
                                    scale="lg"
                                    isDisabled={!(perms.canWrite && network.contract && !network.isReference)}
                                    onClick={notInPrototype}
                                />
                            </span>
                        </NJTooltip>
                        <NJTooltip text="Dupliquer">
                            <NJIconButton icon="add_to_photos" label="Dupliquer" variant="tertiary" scale="lg" onClick={notInPrototype} />
                        </NJTooltip>
                    </>
                )}
                {perms.canDelete && (
                    <NJTooltip text="Supprimer">
                        <span>
                            <NJIconButton
                                icon="delete_outline"
                                label="Supprimer"
                                variant="tertiary"
                                scale="lg"
                                isDisabled={network.isReference && network.status === 'Actif'}
                                onClick={notInPrototype}
                            />
                        </span>
                    </NJTooltip>
                )}
            </div>
        </div>
    );

    let body;
    if (isLoading || !network || !perms) {
        body = <Loading text="Chargement du réseau" scale="md" />;
    } else if (!perms.canRead) {
        body = (
            <div style={{ padding: '0 16px 16px' }}>
                <NJInlineMessage variant="warning" title="Accès refusé">
                    {viewer.user} n'a plus accès à ce réseau : aucun de ses groupes ne figure dans « Consultable par » ou « Modifiable par ». Repassez en
                    Propriétaire pour rétablir l'accès, ou réinitialisez les données.
                </NJInlineMessage>
            </div>
        );
    } else {
        body = (
            <div style={{ padding: '0 16px 16px' }}>
                <Size2MixAccordion>
                    <div>
                        Réseau créé par {network.owner} le {network.createdAt}, dernière modification, {network.updatedAt}
                    </div>
                    <Size2MixSection title="Informations du réseau">
                        <Size2MixInfoGrid
                            rows={[
                                {
                                    label: 'Nom',
                                    value: (
                                        <InlineEditable
                                            canEdit={canEdit}
                                            label="Nom"
                                            display={network.name}
                                            renderEditor={() => (
                                                <div style={{ width: 320 }}>
                                                    <NJFormItem id="network-name" label="Nom" size="sm" value={draftName} autoFocus onChange={(e) => setDraftName((e.target as HTMLInputElement).value)} />
                                                </div>
                                            )}
                                            onSubmit={async () => {
                                                if (!draftName.trim()) {
                                                    errorToast('Le nom ne peut pas être vide', 'Erreur de validation');
                                                    return false;
                                                }
                                                if (draftName === network.name) return true;
                                                return submitWithToast(() => patch({ name: draftName.trim() }), 'Nom modifié avec succès');
                                            }}
                                            onStart={() => setDraftName(network.name)}
                                        />
                                    ),
                                },
                                {
                                    label: 'Description',
                                    value: (
                                            <InlineEditable
                                                canEdit={canEdit}
                                                label="Description"
                                                display={network.description || undefined}
                                                renderEditor={() => (
                                                    <div style={{ width: 460 }}>
                                                        <NJFormItem
                                                            id="network-description"
                                                            label="Description"
                                                            isMultiline
                                                            rows={2}
                                                            maxLength={DESCRIPTION_MAX}
                                                            placeholder="Ajouter une description..."
                                                            value={draftDescription}
                                                            subscriptMessage={`${draftDescription.length}/${DESCRIPTION_MAX} caractères`}
                                                            onChange={(e) => setDraftDescription((e.target as HTMLTextAreaElement).value)}
                                                        />
                                                    </div>
                                                )}
                                                onSubmit={() =>
                                                    draftDescription === network.description
                                                        ? Promise.resolve(true)
                                                        : submitWithToast(() => patch({ description: draftDescription }), 'Description modifiée avec succès')
                                                }
                                                onStart={() => setDraftDescription(network.description)}
                                            />
                                    ),
                                },
                                {
                                    label: 'Fuseau horaire',
                                    value: (
                                        <InlineEditable
                                            canEdit={canEdit}
                                            label="Fuseau horaire"
                                            display={network.timezone}
                                            renderEditor={() => (
                                                <div style={{ width: 320 }}>
                                                    <NJAutocomplete
                                                        id="network-timezone"
                                                        label="Fuseau horaire"
                                                        data={(options?.timezones ?? []).map((tz) => ({ name: tz, value: tz }))}
                                                        value={draftTimezone}
                                                        onChange={setDraftTimezone}
                                                        listLabel="Fuseaux horaires"
                                                        inputInstructions="Utilisez les flèches haut/bas puis Entrée pour sélectionner"
                                                        resultsCountMessage={(n) => `${n} résultat${n > 1 ? 's' : ''}`}
                                                    />
                                                </div>
                                            )}
                                            onSubmit={async () => {
                                                if (!draftTimezone || draftTimezone.value === network.timezone) return true;
                                                return submitWithToast(() => patch({ timezone: draftTimezone.value }), 'Fuseau horaire mis à jour avec succès');
                                            }}
                                            onStart={() => setDraftTimezone({ name: network.timezone, value: network.timezone })}
                                        />
                                    ),
                                },
                                {
                                    label: 'Statut',
                                    value: (
                                        <InlineEditable
                                            canEdit={canEdit}
                                            label="Statut"
                                            display={<Size2MixStatusTag label={network.status} size="lg" />}
                                            renderEditor={() => (
                                                <div style={{ width: 200 }}>
                                                    <NJSelect.Root
                                                        id="network-status"
                                                        label="Statut"
                                                        listNavigationLabel="Utilisez les flèches haut/bas puis Entrée pour sélectionner"
                                                        buttonDefaultValueLabel="Sélectionner un statut"
                                                        initialValue={network.status}
                                                        onChange={(v) => v && setDraftStatus(v as NetworkStatus)}
                                                    >
                                                        {STATUSES.map((s) => (
                                                            <NJSelect.Item key={s} value={s}>{s}</NJSelect.Item>
                                                        ))}
                                                    </NJSelect.Root>
                                                </div>
                                            )}
                                            onSubmit={async () => {
                                                if (draftStatus === network.status) return true;
                                                return submitWithToast(() => patch({ status: draftStatus }), draftStatus === 'Actif' ? 'Réseau activé avec succès' : 'Réseau archivé avec succès');
                                            }}
                                            onStart={() => setDraftStatus(network.status)}
                                        />
                                    ),
                                },
                                {
                                    label: "Phase d'étude",
                                    value: (
                                        <InlineEditable
                                            canEdit={canEdit}
                                            label="Phase d'étude"
                                            display={<Size2MixStatusTag label={network.studyPhase} variant={STUDY_PHASE_VARIANT[network.studyPhase]} size="lg" />}
                                            renderEditor={() => (
                                                <div style={{ width: 220 }}>
                                                    <NJSelect.Root
                                                        id="network-study-phase"
                                                        label="Phase d'étude"
                                                        listNavigationLabel="Utilisez les flèches haut/bas puis Entrée pour sélectionner"
                                                        buttonDefaultValueLabel="Sélectionner une phase"
                                                        initialValue={network.studyPhase}
                                                        onChange={(v) => v && setDraftPhase(v as StudyPhase)}
                                                    >
                                                        {STUDY_PHASES.map((p) => (
                                                            <NJSelect.Item key={p} value={p}>{p}</NJSelect.Item>
                                                        ))}
                                                    </NJSelect.Root>
                                                </div>
                                            )}
                                            onSubmit={async () => {
                                                if (draftPhase === network.studyPhase) return true;
                                                return submitWithToast(() => patch({ studyPhase: draftPhase }), "Phase d'étude modifiée avec succès");
                                            }}
                                            onStart={() => setDraftPhase(network.studyPhase)}
                                        />
                                    ),
                                },
                                {
                                    label: 'Contrat',
                                    value: network.contract ? (
                                        <AccessTag label={network.contract} onDelete={canEdit ? () => setConfirmUnlink(true) : undefined} />
                                    ) : canEdit ? (
                                        <AddWithAutocomplete
                                            id="network-contract"
                                            label="Associer un contrat"
                                            options={options?.contracts ?? []}
                                            onAdd={(c) => submitWithToast(() => patch({ contract: c }), 'Contrat associé avec succès')}
                                        />
                                    ) : (
                                        <span style={{ color: 'var(--nj-core-color-grey-500)' }}>-</span>
                                    ),
                                },
                                {
                                    label: 'Propriétaire',
                                    value: (
                                        <AccessList
                                            id="network-owners"
                                            items={network.owners}
                                            canEdit={canEdit}
                                            addLabel="Ajouter un propriétaire"
                                            options={options?.users ?? []}
                                            onAdd={(v) => addAccess('owners', v)}
                                            onRemove={(v) => removeAccess('owners', v)}
                                        />
                                    ),
                                },
                                {
                                    label: 'Consultable par',
                                    value: (
                                        <AccessList
                                            id="network-readers"
                                            items={network.readers}
                                            canEdit={canEdit}
                                            addLabel="Ajouter un rôle en lecture"
                                            options={options?.roles ?? []}
                                            onAdd={(v) => addAccess('readers', v)}
                                            onRemove={(v) => removeAccess('readers', v)}
                                        />
                                    ),
                                },
                                {
                                    label: 'Modifiable par',
                                    value: (
                                        <AccessList
                                            id="network-editors"
                                            items={network.editors}
                                            canEdit={canEdit}
                                            addLabel="Ajouter un rôle en écriture"
                                            options={options?.roles ?? []}
                                            onAdd={(v) => addAccess('editors', v)}
                                            onRemove={(v) => removeAccess('editors', v)}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Size2MixSection>
                    <div
                        role="note"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            marginTop: 24,
                            padding: '16px',
                            background: 'var(--nj-semantic-color-background-status-information-tertiary-default)',
                            border: '1px solid var(--nj-semantic-color-border-status-information-moderate-default)',
                        }}
                    >
                        <span
                            aria-hidden
                            className="material-icons"
                            style={{ fontFamily: 'Material Icons', fontSize: 20, color: 'var(--nj-semantic-color-icon-status-information-primary-default)' }}
                        >
                            info
                        </span>
                        <NJText scale="sm">
                            Pour accéder aux Paramètres et à la Synthèse du réseau, rendez-vous sur la page Topologie.{' '}
                            <a
                                href="#"
                                style={{ color: 'var(--nj-semantic-color-text-status-information-primary-default)', textDecoration: 'underline' }}
                                onClick={(event) => { event.preventDefault(); nav.onNetworkNavigate('topology'); }}
                            >
                                Cliquez ici
                            </a>
                        </NJText>
                    </div>
                </Size2MixAccordion>
            </div>
        );
    }

    return (
        <PrototypeStudioLayout title={meta.title}>
            <NJToastContainer />
            <ViewerSwitcher
                viewer={viewerId}
                onChange={setViewer}
                perms={perms}
                onReset={() => reset.mutate(undefined, { onSuccess: () => successToast('Données du prototype réinitialisées') })}
            />
            <NetworkShell
                network={{ name: network?.name ?? 'test PUC', fluidType: 'hot_water' }}
                activePage="general"
                onNavigate={nav.onNetworkNavigate}
                onPrimaryNavigate={nav.onPrimaryNavigate}
            >
                {toolbar}
                {body}
            </NetworkShell>

            <NJModal isOpen={confirmUnlink} setIsOpen={setConfirmUnlink}>
                <NJModalContentInformation id="unlink-contract-modal" iconVariant="warning" centeredFooter title="Dissocier le contrat" onClose={() => setConfirmUnlink(false)}>
                    <div style={{ marginBottom: 24 }}>
                        <NJText scale="md">Êtes-vous sûr de vouloir dissocier ce contrat ?</NJText>
                    </div>
                    <NJButton label="Annuler" variant="secondary" onClick={() => setConfirmUnlink(false)} />
                    <NJButton
                        label="Je confirme"
                        variant="primary"
                        onClick={() => {
                            setConfirmUnlink(false);
                            void submitWithToast(() => patch({ contract: '' }), 'Contrat dissocié avec succès');
                        }}
                    />
                </NJModalContentInformation>
            </NJModal>
        </PrototypeStudioLayout>
    );
}
